/**
 * useMemberships Hook - Phase 2C-2
 * 
 * Fetches user's pool memberships.
 * Uses existing UserService pattern.
 */

import { useState, useEffect, useCallback } from 'react';
import { PoolMembership } from '../types';
import { UserService, ApiError } from '../api';

interface UseMembershipsResult {
  memberships: PoolMembership[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  getMembershipForPool: (poolId: string) => PoolMembership | undefined;
}

export function useMemberships(userId: string): UseMembershipsResult {
  const [memberships, setMemberships] = useState<PoolMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMemberships = useCallback(async () => {
    if (!userId || userId === 'guest') {
      setMemberships([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const data = await UserService.getMemberships(userId);
      setMemberships(data);
    } catch (err) {
      // 404 means no memberships, not an error
      if (err instanceof ApiError && err.status === 404) {
        setMemberships([]);
      } else {
        setError(err instanceof ApiError ? err.message : 'Failed to load memberships');
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  const getMembershipForPool = useCallback(
    (poolId: string) => memberships.find(m => m.pool_id === poolId),
    [memberships]
  );

  return { memberships, loading, error, refetch: fetchMemberships, getMembershipForPool };
}
